import type { Maybe } from '@adornis/base/utilTypes.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { Collapse } from '@adornis/components/client/Collapse.js';
import { html } from 'lit';
import { Beschaeftigungsart } from '../../../../db/static-data/beschaeftigungsart.js';
import { BetrGrAV } from '../../../../db/static-data/betr-gr-av.js';
import { BetrGrKV } from '../../../../db/static-data/betr-gr-kv.js';
import { BetrGrPV } from '../../../../db/static-data/betr-gr-pv.js';
import { BetrGrRV } from '../../../../db/static-data/betr-gr-rv.js';
import { ErgaenzungSV } from '../../../../db/static-data/ergaenzung-sv.js';
import { GeringfuegigeBeschaeftigung } from '../../../../db/static-data/geringfuegige-beschaeftigung.js';
import { KVAGZuschuss } from '../../../../db/static-data/kv-ag-zuschuss.js';
import { Mehrfachbeschaeftigung } from '../../../../db/static-data/mehrfachbeschaeftigung.js';
import { PrivatVersichtert } from '../../../../db/static-data/privat-versichert.js';
import { PVAGZuschuss } from '../../../../db/static-data/pv-ag-zuschuss.js';
import { PVGesamtbeitrag } from '../../../../db/static-data/pv-gesamtbeitrag.js';
import { Rentner } from '../../../../db/static-data/rentner.js';
import { getHistoryForPath } from '../../../api/getHistoryForPath.js';
import { getReadableHistoryStrings } from '../../../api/getReadableHistoryStrings.js';
import type { AppliedContract } from '../../../entities/AppliedContract.js';
import type { ContractForm } from './sharedContractEditParts.js';

interface ContractEditSocialSecuritySectionProps {
  form: ContractForm;
  contract: Maybe<AppliedContract>;
}

type PaisyEntry = { 'PAISY Wert': number; Bezeichnung: string };

const paisyRenderString = (values: readonly PaisyEntry[]) => (v: Maybe<number>) => {
  const val = values.find(value => value['PAISY Wert'] === v);
  return val ? `${val['PAISY Wert']} ${val.Bezeichnung}` : '';
};

/** Sozialversicherung of the expert contract edit */
export const ContractEditSocialSecuritySection = stateful((props: ContractEditSocialSecuritySectionProps) => {
  const historyFor = (path: string) => getReadableHistoryStrings(getHistoryForPath(props.contract?.history, path));

  const groupKeyString = `${props.form.document?.agSozialVersicherung?.beitragsgruppenschluesselKV ?? '?'}${
    props.form.document?.agSozialVersicherung?.beitragsgruppenschluesselRV ?? '?'
  }${props.form.document?.agSozialVersicherung?.beitragsgruppenschluesselAV ?? '?'}${
    props.form.document?.agSozialVersicherung?.beitragsgruppenschluesselPV ?? '?'
  }`;

  const booleanToggle = (field: 'altfallGleiter' | 'applyMidijob' | 'krankentagegeld' | 'leiharbeitsverhaeltnis') =>
    ToggleInput({
      ...props.form.field('agSozialVersicherung', field),
      label: props.form.document?.agSozialVersicherung?.[field] ? 'Ja' : 'Nein',
    });

  return html`
    ${Collapse({
      className: 'border-0 bg-transparent shadow-none',
      heading: html`<h2>Sozialversicherung</h2>`,
      defaultOpen: false,
      content: html`
        <div class="grid grid-cols-1 md:grid-cols-[1fr_2fr_1fr_2fr] items-center gap-2">
          <h3 class="col-span-full font-semibold">Beitragsgruppenschluessel: (${groupKeyString})</h3>

          ${FormGridEntry({
            label: 'KV (Krankenversicherung)',
            render: DropdownSelectionInput({
              ...props.form.field('agSozialVersicherung', 'beitragsgruppenschluesselKV'),
              selectables: Object.values(BetrGrKV).map(b => b['PAISY Wert']),
              renderString: paisyRenderString(BetrGrKV),
              placeholder: 'Nichts ausgewählt',
              clearable: true,
            }),
            history: historyFor('agSozialVersicherung.beitragsgruppenschluesselKV'),
          })}
          ${FormGridEntry({
            label: 'RV (Rentenversicherung)',
            render: DropdownSelectionInput({
              ...props.form.field('agSozialVersicherung', 'beitragsgruppenschluesselRV'),
              selectables: Object.values(BetrGrRV).map(b => b['PAISY Wert']),
              renderString: paisyRenderString(BetrGrRV),
              placeholder: 'Nichts ausgewählt',
              clearable: true,
            }),
            history: historyFor('agSozialVersicherung.beitragsgruppenschluesselRV'),
          })}
          ${FormGridEntry({
            label: 'AV (Arbeitslosenversicherung)',
            render: DropdownSelectionInput({
              ...props.form.field('agSozialVersicherung', 'beitragsgruppenschluesselAV'),
              selectables: Object.values(BetrGrAV).map(b => b['PAISY Wert']),
              renderString: paisyRenderString(BetrGrAV),
              placeholder: 'Nichts ausgewählt',
              clearable: true,
            }),
            history: historyFor('agSozialVersicherung.beitragsgruppenschluesselAV'),
          })}
          ${FormGridEntry({
            label: 'PV (Pflegeversicherung)',
            render: DropdownSelectionInput({
              ...props.form.field('agSozialVersicherung', 'beitragsgruppenschluesselPV'),
              selectables: Object.values(BetrGrPV).map(b => b['PAISY Wert']),
              renderString: paisyRenderString(BetrGrPV),
              placeholder: 'Nichts ausgewählt',
              clearable: true,
            }),
            history: historyFor('agSozialVersicherung.beitragsgruppenschluesselPV'),
          })}

          <hr class="col-span-full border-base-300" />

          ${FormGridEntry({
            label: 'Mehrfachbeschäftigung',
            render: DropdownSelectionInput({
              ...props.form.field('agSozialVersicherung', 'mehrfachbeschaeftigung'),
              selectables: Object.values(Mehrfachbeschaeftigung).map(m => m.Bezeichnung),
              placeholder: 'Keine Mehrfachbeschäftigung',
              clearable: true,
            }),
            history: historyFor('agSozialVersicherung.mehrfachbeschaeftigung'),
          })}
          ${FormGridEntry({
            label: 'Beschäftigungsart',
            render: DropdownSelectionInput({
              ...props.form.field('agSozialVersicherung', 'beschaeftigungsart'),
              selectables: Object.values(Beschaeftigungsart).map(ba => ba.Bezeichnung),
              placeholder: 'Beschäftigungsart',
              clearable: true,
            }),
            history: historyFor('agSozialVersicherung.beschaeftigungsart'),
          })}
          ${FormGridEntry({
            label: 'KV Arbeitgeberzuschuss',
            render: DropdownSelectionInput({
              ...props.form.field('agSozialVersicherung', 'kvAGZuschuss'),
              selectables: Object.values(KVAGZuschuss).map(z => z.Bezeichnung),
              placeholder: 'Nichts ausgewählt',
              clearable: true,
            }),
            history: historyFor('agSozialVersicherung.kvAGZuschuss'),
          })}
          ${FormGridEntry({
            label: 'PV Arbeitgeberzuschuss',
            render: DropdownSelectionInput({
              ...props.form.field('agSozialVersicherung', 'pvAGZuschuss'),
              selectables: Object.values(PVAGZuschuss).map(z => z.Bezeichnung),
              placeholder: 'Nichts ausgewählt',
              clearable: true,
            }),
            history: historyFor('agSozialVersicherung.pvAGZuschuss'),
          })}
          ${FormGridEntry({
            label: 'PV Gesamtbeitrag',
            render: DropdownSelectionInput({
              ...props.form.field('agSozialVersicherung', 'pvGesamtbeitrag'),
              selectables: Object.values(PVGesamtbeitrag).map(b => b.Bezeichnung),
              placeholder: 'Nichts ausgewählt',
              clearable: true,
            }),
            history: historyFor('agSozialVersicherung.pvGesamtbeitrag'),
          })}
          ${FormGridEntry({
            label: 'Altfall Gleiter',
            render: booleanToggle('altfallGleiter'),
            history: historyFor('agSozialVersicherung.altfallGleiter'),
          })}
          ${FormGridEntry({
            label: 'Rentner',
            render: DropdownSelectionInput({
              ...props.form.field('agSozialVersicherung', 'rentner'),
              selectables: Object.values(Rentner).map(r => r.Bezeichnung),
              placeholder: 'Nichts ausgewählt',
              clearable: true,
            }),
            history: historyFor('agSozialVersicherung.rentner'),
          })}
          ${FormGridEntry({
            label: 'Midijobregelung anwenden',
            render: booleanToggle('applyMidijob'),
            history: historyFor('agSozialVersicherung.applyMidijob'),
          })}
          ${FormGridEntry({
            label: 'Privat versichert',
            render: DropdownSelectionInput({
              ...props.form.field('agSozialVersicherung', 'privatVersichert'),
              selectables: Object.values(PrivatVersichtert).map(v => String(v.Bezeichnung)),
              placeholder: 'Privat versichert',
              clearable: true,
            }),
            history: historyFor('agSozialVersicherung.privatVersichert'),
          })}
          ${FormGridEntry({
            label: 'Krankentagegeld',
            render: booleanToggle('krankentagegeld'),
            history: historyFor('agSozialVersicherung.krankentagegeld'),
          })}
          ${FormGridEntry({
            label: 'Leiharbeitsverhältnis',
            render: booleanToggle('leiharbeitsverhaeltnis'),
            history: historyFor('agSozialVersicherung.leiharbeitsverhaeltnis'),
          })}
          ${FormGridEntry({
            label: 'Ergänzung SV',
            render: DropdownSelectionInput({
              ...props.form.field('agSozialVersicherung', 'ergaenzungSV'),
              selectables: Object.values(ErgaenzungSV).map(sv => sv.Bezeichnung),
              placeholder: 'Nichts ausgewählt',
              clearable: true,
            }),
            history: historyFor('agSozialVersicherung.ergaenzungSV'),
          })}
          ${FormGridEntry({
            label: 'Geringfügige Beschäftigung',
            render: DropdownSelectionInput({
              ...props.form.field('agSozialVersicherung', 'geringfuegigeBeschaeftigung'),
              selectables: Object.values(GeringfuegigeBeschaeftigung).map(b => b.Bezeichnung),
              placeholder: 'Nichts ausgewählt',
              clearable: true,
            }),
            history: historyFor('agSozialVersicherung.geringfuegigeBeschaeftigung'),
          })}
        </div>
      `,
    })}
  `;
});
